export interface UniversityProgram {
  university: string;
  program: string;
  reason: string;
}

export interface UniversityIdentity {
  initials: string;
  color: "blue" | "gray" | "green" | "orange";
  domain?: string;
}

/**
 * Identity per university — initials, a palette colour drawn only from
 * blue / gray / green / orange (the project's restricted palette), and an
 * optional domain used to source a logo PNG via Google's favicon service
 * (https://www.google.com/s2/favicons?domain=<domain>&sz=128). The Avatar
 * falls back to the initials if the logo image fails to load.
 *
 * TODO(SD-837): replace the Google-favicons URL with locally hosted PNGs
 * (under public/img/universities/) once the program list stabilises. Favicons
 * are tiny and depend on Google's service uptime; locally hosted logos are
 * higher resolution and offline-safe (which matters for the Print/PDF flow).
 * Also confirm logo licensing before shipping outside Boston-area examples.
 */
const DEFAULT_IDENTITY: UniversityIdentity = { initials: "U", color: "gray" };

export const UNIVERSITY_IDENTITY: Record<string, UniversityIdentity> = {
  "Massachusetts Institute of Technology": { initials: "MIT", color: "blue", domain: "mit.edu" },
  "Harvard University": { initials: "H", color: "blue", domain: "harvard.edu" },
  "Harvard Medical School": { initials: "HMS", color: "blue", domain: "hms.harvard.edu" },
  "Harvard Law School": { initials: "HLS", color: "blue", domain: "hls.harvard.edu" },
  "Harvard Business School": { initials: "HBS", color: "blue", domain: "hbs.edu" },
  "Harvard Graduate School of Education": {
    initials: "HGSE",
    color: "blue",
    domain: "gse.harvard.edu",
  },
  "MIT Sloan": { initials: "MIT", color: "blue", domain: "mitsloan.mit.edu" },
  "MIT Sloan School of Management": { initials: "MIT", color: "blue", domain: "mitsloan.mit.edu" },
  "Boston University": { initials: "BU", color: "gray", domain: "bu.edu" },
  "Boston University Questrom": { initials: "BU", color: "gray", domain: "bu.edu" },
  "Boston University School of Medicine": {
    initials: "BU",
    color: "gray",
    domain: "bumc.bu.edu",
  },
  "Boston University School of Law": { initials: "BU", color: "gray", domain: "bu.edu" },
  "Tufts University": { initials: "T", color: "blue", domain: "tufts.edu" },
  "Tufts University School of Medicine": {
    initials: "T",
    color: "blue",
    domain: "medicine.tufts.edu",
  },
  "Northeastern University": { initials: "NU", color: "blue", domain: "northeastern.edu" },
  "Boston College": { initials: "BC", color: "gray", domain: "bc.edu" },
  "Boston College Law School": { initials: "BC", color: "gray", domain: "bc.edu" },
  "Brandeis University": { initials: "B", color: "blue", domain: "brandeis.edu" },
  "Babson College": { initials: "BB", color: "orange", domain: "babson.edu" },
  "Bentley University": { initials: "BE", color: "orange", domain: "bentley.edu" },
  "Emerson College": { initials: "E", color: "green", domain: "emerson.edu" },
  "Suffolk University": { initials: "S", color: "gray", domain: "suffolk.edu" },
  "Suffolk University Law School": { initials: "S", color: "gray", domain: "suffolk.edu" },
  "Suffolk University Sawyer": { initials: "S", color: "gray", domain: "suffolk.edu" },
  "Wentworth Institute of Technology": { initials: "W", color: "orange", domain: "wit.edu" },
  "Lesley University": { initials: "L", color: "green", domain: "lesley.edu" },
};

export function identityForUniversity(name: string): UniversityIdentity {
  return UNIVERSITY_IDENTITY[name] ?? DEFAULT_IDENTITY;
}

/**
 * Returns a PNG logo URL for a given university via Google's favicon service.
 * Returns undefined if the university has no domain mapping.
 */
export function logoUrlForUniversity(name: string): string | undefined {
  const identity = UNIVERSITY_IDENTITY[name];
  if (!identity?.domain) return undefined;
  return `https://www.google.com/s2/favicons?domain=${identity.domain}&sz=128`;
}

/**
 * Example programs at private universities in the greater Boston area, mapped to
 * profession keys from `professions.ts`. Curated as illustrative starting points
 * for student exploration only — not endorsements, admissions advice, or a
 * comprehensive list. Programs and offerings change; verify on each university's
 * official site before relying on any entry here.
 *
 * TODO(SD-837): this dataset is geographically limited to Boston-area private
 * universities. Before shipping to other markets:
 *   - generalise the structure to support multiple regions / countries
 *   - source from a structured catalogue rather than hand-curated entries
 *   - keep public-uni programs out only as long as the report is explicitly
 *     scoped to private institutions
 *   - revisit the per-school "reason" sentences quarterly as offerings change
 */
export const BOSTON_PROGRAMS_BY_PROFESSION: Record<string, UniversityProgram[]> = {
  research: [
    {
      university: "Massachusetts Institute of Technology",
      program: "Undergraduate Research Opportunities Program (UROP)",
      reason:
        "Pairs students with active labs from first year — one of the longest-running undergraduate research programs in the country.",
    },
    {
      university: "Harvard University",
      program: "Faculty of Arts & Sciences research labs",
      reason:
        "Direct access to faculty-led research across natural sciences, social sciences, and humanities.",
    },
    {
      university: "Boston University",
      program: "Undergraduate Research Opportunities Program",
      reason: "Funded research positions every semester, structured for early undergraduates.",
    },
  ],
  engineering: [
    {
      university: "Massachusetts Institute of Technology",
      program: "School of Engineering",
      reason:
        "Hands-on project culture with strong undergraduate research and a curriculum that ties theory to building.",
    },
    {
      university: "Northeastern University",
      program: "College of Engineering",
      reason:
        "Co-op program places students in industry roles for six or more months as part of the degree.",
    },
    {
      university: "Tufts University",
      program: "School of Engineering",
      reason:
        "Smaller cohort with cross-disciplinary tracks like human factors and engineering psychology.",
    },
  ],
  medicine: [
    {
      university: "Harvard Medical School",
      program: "MD Program",
      reason:
        "Affiliated teaching hospitals (MGH, Brigham, BIDMC) provide clinical exposure across nearly every specialty.",
    },
    {
      university: "Tufts University School of Medicine",
      program: "MD Program",
      reason:
        "Clinical training across community and academic hospitals; strong primary-care and global-health tracks.",
    },
    {
      university: "Boston University School of Medicine",
      program: "MD Program",
      reason:
        "Boston Medical Center is the largest safety-net hospital in New England, exposing students to a broad patient population.",
    },
  ],
  law: [
    {
      university: "Harvard Law School",
      program: "JD Program",
      reason:
        "Largest course catalog of any U.S. law school; clinics from criminal justice to international human rights.",
    },
    {
      university: "Boston College Law School",
      program: "JD Program",
      reason:
        "Smaller cohort with strong cohort culture; respected for public-interest law training.",
    },
    {
      university: "Boston University School of Law",
      program: "JD Program",
      reason: "Strong in tax, IP, and banking law; competitive moot-court program.",
    },
    {
      university: "Suffolk University Law School",
      program: "JD Program",
      reason:
        "Boston's largest part-time/evening JD program; strong trial advocacy and legal-clinic offerings.",
    },
  ],
  software: [
    {
      university: "Massachusetts Institute of Technology",
      program: "EECS — Computer Science & Engineering",
      reason:
        "6-3 / 6-7 majors combine CS with math, AI, and biology; rigorous theory-plus-systems sequence.",
    },
    {
      university: "Harvard University",
      program: "Paulson School of Engineering — Computer Science",
      reason: "Theory-strong CS curriculum with cross-registration into MIT classes.",
    },
    {
      university: "Northeastern University",
      program: "Khoury College of Computer Sciences",
      reason:
        "Co-op program places CS students at major tech firms during the degree; strong systems/security tracks.",
    },
  ],
  strategy: [
    {
      university: "Harvard Business School",
      program: "MBA Program",
      reason: "Case-method instruction across industries; strong general-management foundation.",
    },
    {
      university: "MIT Sloan",
      program: "MBA — Strategy & Innovation",
      reason:
        "Quantitative-leaning MBA with strong tracks in operations, analytics, and entrepreneurship.",
    },
    {
      university: "Boston University Questrom",
      program: "MBA Program",
      reason: "STEM-designated MBA with hands-on consulting projects and digital-tech focus.",
    },
  ],
  journalism: [
    {
      university: "Emerson College",
      program: "BS in Journalism",
      reason: "Heavy hands-on reporting; partnerships with Boston-area newsrooms.",
    },
    {
      university: "Boston University",
      program: "College of Communication — Journalism",
      reason: "Strong in broadcast and investigative; faculty include working journalists.",
    },
    {
      university: "Northeastern University",
      program: "School of Journalism",
      reason: "Co-op puts students in newsrooms (Boston Globe, NPR) before graduation.",
    },
  ],
  editing: [
    {
      university: "Emerson College",
      program: "BFA in Writing, Literature & Publishing",
      reason: "Combines writing workshops with publishing-industry coursework.",
    },
    {
      university: "Boston University",
      program: "MFA in Creative Writing",
      reason: "Selective workshop-based program with prominent visiting writers.",
    },
  ],
  translation: [
    {
      university: "Boston University",
      program: "MA in Translation Studies",
      reason:
        "Combines translation theory and practice; languages include French, Spanish, Italian, German.",
    },
    {
      university: "Brandeis University",
      program: "Romance Studies — Translation",
      reason: "Translation embedded in a literary and comparative-studies program.",
    },
  ],
  education: [
    {
      university: "Lesley University",
      program: "Graduate School of Education",
      reason:
        "Practitioner-focused EdM and licensure tracks; strong in early childhood and special education.",
    },
    {
      university: "Boston College",
      program: "Lynch School of Education & Human Development",
      reason: "Rigorous research training in education policy, counseling, and curriculum.",
    },
    {
      university: "Harvard Graduate School of Education",
      program: "EdM Programs",
      reason:
        "One-year master's with concentrations from education policy to learning design and tech.",
    },
  ],
  communications: [
    {
      university: "Emerson College",
      program: "Communication Studies",
      reason: "Specializes in media production; strong placement in TV/film/PR.",
    },
    {
      university: "Boston University",
      program: "College of Communication",
      reason: "PR, advertising, journalism, and emerging-media tracks; ad-agency partnerships.",
    },
    {
      university: "Suffolk University",
      program: "Communication & Journalism",
      reason: "Smaller program with hands-on PR campaigns and media labs.",
    },
  ],
  paralegal: [
    {
      university: "Suffolk University",
      program: "Paralegal Studies",
      reason: "ABA-approved paralegal certificate; strong placement in Boston firms.",
    },
    {
      university: "Boston University",
      program: "Center for Professional Education — Paralegal",
      reason: "Continuing-education certificate aimed at career-changers.",
    },
  ],
  finance: [
    {
      university: "Harvard Business School",
      program: "MBA — Finance",
      reason:
        "Case-driven training with elective depth across investment management, corporate finance, and PE/VC.",
    },
    {
      university: "MIT Sloan",
      program: "Master of Finance",
      reason:
        "12–18 month quantitative finance master's; strong recruiting at hedge funds and banks.",
    },
    {
      university: "Babson College",
      program: "BS in Finance",
      reason: "Entrepreneurship focus applied to corporate finance; required practitioner courses.",
    },
    {
      university: "Bentley University",
      program: "BS in Finance",
      reason: "Strong in equities and corporate finance; required Bloomberg-lab work.",
    },
  ],
  accounting: [
    {
      university: "Babson College",
      program: "BS in Accounting",
      reason: "Combines accounting with the broader entrepreneurship core.",
    },
    {
      university: "Bentley University",
      program: "BS in Accountancy",
      reason: "Heavy in audit and tax; strong recruiting from Big Four firms.",
    },
    {
      university: "Boston University Questrom",
      program: "BSBA in Accounting",
      reason: "AACSB-accredited with electives that support a CPA-track curriculum.",
    },
    {
      university: "Suffolk University Sawyer",
      program: "BSBA in Accounting",
      reason: "Smaller cohort with strong CPA-exam pass rates.",
    },
  ],
  dataAnalysis: [
    {
      university: "Massachusetts Institute of Technology",
      program: "Statistics & Data Science",
      reason:
        "Cross-departmental minor combining stats, ML, and computation; available alongside any major.",
    },
    {
      university: "Northeastern University",
      program: "Khoury — Data Science",
      reason:
        "Combines CS systems training with statistical methods; co-op offers industry data work.",
    },
    {
      university: "Boston University",
      program: "MS in Statistical Practice",
      reason: "Applied-stats master's with a consulting capstone.",
    },
    {
      university: "Bentley University",
      program: "MS in Business Analytics",
      reason: "Tech-business hybrid with a required practicum on a real corporate dataset.",
    },
  ],
  sciences: [
    {
      university: "Massachusetts Institute of Technology",
      program: "School of Science",
      reason:
        "Top-ranked departments in physics, biology, math, and chemistry; high research access.",
    },
    {
      university: "Harvard University",
      program: "Faculty of Arts & Sciences",
      reason:
        "Broad concentrations including molecular & cellular biology, chemistry, physics, neuroscience.",
    },
    {
      university: "Boston University",
      program: "College of Arts & Sciences",
      reason: "Strong in biology and physics; access to BU's research centers.",
    },
    {
      university: "Tufts University",
      program: "School of Arts & Sciences",
      reason: "Smaller class sizes; cross-disciplinary tracks like quantitative biology.",
    },
  ],
  surgery: [
    {
      university: "Harvard Medical School",
      program: "Surgery Residency",
      reason: "Training across MGH and Brigham programs; broad specialty exposure.",
    },
    {
      university: "Tufts University School of Medicine",
      program: "Surgery Residency",
      reason: "Community-hospital-based training with significant operative volume.",
    },
    {
      university: "Boston University School of Medicine",
      program: "Surgery Residency",
      reason: "BMC's trauma center provides high-volume, high-acuity operative experience.",
    },
  ],
  projectCoordination: [
    {
      university: "Harvard Business School",
      program: "MBA — General Management",
      reason:
        "Case method develops cross-functional decision-making for managing programs and teams.",
    },
    {
      university: "Boston University Questrom",
      program: "MBA — Operations",
      reason: "Operations elective track aligned with project leadership in tech and healthcare.",
    },
    {
      university: "Bentley University",
      program: "MBA — Operations & Project Management",
      reason: "Practitioner-faculty MBA with explicit project-management curriculum.",
    },
  ],
  design: [
    {
      university: "Northeastern University",
      program: "College of Arts, Media & Design",
      reason: "Co-op design education with majors in graphic design, UX, and game design.",
    },
    {
      university: "Boston University",
      program: "College of Fine Arts — Design",
      reason: "Combines studio practice with critical theory; small cohort, intensive crit.",
    },
    {
      university: "Emerson College",
      program: "Visual & Media Arts",
      reason: "Production-heavy degree with strong placement in film, TV, motion design.",
    },
  ],
  sports: [
    {
      university: "Boston University",
      program: "Sargent College of Health & Rehabilitation",
      reason:
        "Health-sciences college with strong programs in PT, athletic training, and human movement.",
    },
    {
      university: "Boston College",
      program: "Lynch School — Applied Psychology & Human Development",
      reason:
        "Sport-psychology and youth-development tracks within a broader applied-psych curriculum.",
    },
    {
      university: "Northeastern University",
      program: "Bouve College of Health Sciences",
      reason: "PT, athletic training, exercise science with co-op clinical placements.",
    },
  ],
  skilledTrades: [
    {
      university: "Wentworth Institute of Technology",
      program: "BS in Construction Management",
      reason: "Practical, lab-heavy curriculum with industry partnerships in Boston construction.",
    },
    {
      university: "Wentworth Institute of Technology",
      program: "BS in Mechanical Engineering Technology",
      reason: "Applied engineering degree with co-op model, geared to working roles in industry.",
    },
  ],
  visualArts: [
    {
      university: "Boston University",
      program: "School of Visual Arts",
      reason: "BFA programs in painting, sculpture, graphic design with intensive studio time.",
    },
    {
      university: "Lesley University",
      program: "Lesley Art + Design",
      reason: "BFA programs combined with strong art-education and animation tracks.",
    },
    {
      university: "Emerson College",
      program: "Visual & Media Arts",
      reason: "Film, photo, and animation production with industry placement.",
    },
  ],
};

export function programsForProfession(key: string): UniversityProgram[] {
  return BOSTON_PROGRAMS_BY_PROFESSION[key] ?? [];
}
